'use strict';

define('vb/private/types/defaultSecurityProvider',['vb/types/securityProvider',
  'vb/private/utils',
  'vb/private/stateManagement/router',
  'vb/private/stateManagement/application',
  'vb/private/types/utils/cryptoUtils',
  'urijs/URI',
  'vbsw/private/serviceWorkerManager',
  'vbsw/private/fetchHandler',
  'vbsw/private/pwa/pwaUtils',
  'vbsw/private/constants',
  'vb/private/constants',
  'vbc/private/log',
], (SecurityProvider, Utils, Router, Application, CryptoUtils, URI,
  ServiceWorkerManager, FetchHandler, PwaUtils, SwConstants, Constants, Log) => {
  // the amount of time in milliseconds to wait for the access token from the hidden iframe
  const REFRESH_ACCESS_TOKEN_TIMEOUT = 3000;

  // the id for the hidden iframe for refreshing the access token
  const REFRESH_ACCESS_TOKEN_IFRAME = 'vb-refresh-access-token-iframe';

  const logger = Log.getLogger('/vb/private/types/defaultSecurityProvider');

  /**
   * Provides default security functionality for VB applications.
   *
   * @extends SecurityProvider
   */
  class DefaultSecurityProvider extends SecurityProvider {
    /**
     * Initializes the DefaultSecurityProvider instance with the provided configuration.
     *
     * @param {Object} config - Configuration object containing idcsInfo, authentication, and other settings.
     * @returns {Promise<void>} A promise that resolves when the initialization is complete.
     */
    initialize(config) {
      return Promise.resolve().then(() => {
        this.idcsInfo = config.idcsInfo || {};

        // get the default security configuration
        this.defaultAuthentication = config.authentication;

        // get the override for the JWT clock skew tolerance
        this.jwtClockSkewTolerance = config.jwtClockSkewTolerance;

        const vbConfig = globalThis.vbInitConfig || {};
        this.enableApplicationOAuthFlow = !vbConfig.IS_DT_MODE && config.applicationOAuthFlow === 'authorizationCode';

        if (this.enableApplicationOAuthFlow) {
          this.tokenExchangeUrl = `${config.oauthUrl}/token`;

          // initiate OAuth flow
          return this.vbGetOAuthAccessToken();
        }

        return undefined;
      }).then(() => super.initialize(config));
    }

    /**
     * Fetches the current user information based on the provided configuration.
     *
     * If no URL is specified in the configuration, it derives the URL from the `vbInitConfig`.
     * In non-DT mode, the URL is constructed as `{Router.baseUrl}_currentuser`. In DT mode,
     * the URL is constructed as `{contextRoot}resources/security/user`, where `contextRoot` is obtained from
     * `vbInitConfig`.
     *
     * If OAuth flow is enabled, it uses the `oauthUrl` instead, which accepts a bearer token, and ignores any
     * injected user information.
     *
     * @param {Object} config - Configuration object containing the URL for fetching the current user.
     * @param {String} [config.url] - URL for fetching the current user. If not provided, it will be derived from
     * `vbInitConfig`.
     * @param {String} [config.oauthUrl] - OAuth URL for fetching the current user. Used when OAuth flow is enabled.
     * @param {String} [config.endpoint] - Service endpoint ID. If provided, it takes precedence over `url`.
     * @returns {Promise<Object>} A promise that resolves with the fetched current user information.
     */
    fetchCurrentUser(config) {
      const vbConfig = globalThis.vbInitConfig;

      // if no url or service endpoint ID is provided, derive url from vbInitConfig
      if (!config.url && !config.endpoint) {
        // the url is different between staged/live and DT mode
        if (!vbConfig.IS_DT_MODE) {
          // staged/live
          config.url = `${Router.baseUrl}_currentuser`;
        } else {
          // DT mode
          // get the context root from vbInitConfig and make sure it starts and ends with /
          const contextRoot = this.getContextRoot();

          config.url = `${contextRoot}resources/security/user`;
        }
      }

      // for oauth flow, use oauthUrl instead which accepts bearer token
      if (this.enableApplicationOAuthFlow) {
        config.url = config.oauthUrl;

        // ignore injected user info
        delete vbConfig.INJECTED;
      }

      return super.fetchCurrentUser(config);
    }

    /**
     * Returns information related to custom login configurations.
     *
     * @return {Object} An object containing custom login page information.
     */
    getCustomLoginInfo() {
      return this.idcsInfo.customLoginPageInfo || {};
    }

    /**
     * Returns the context root for the application.
     *
     * @returns {String} The context root for the application, starting and ending with a forward slash (/).
     */
    // eslint-disable-next-line class-methods-use-this
    getContextRoot() {
      const vbConfig = globalThis.vbInitConfig;
      let contextRoot = vbConfig.CONTEXT_ROOT ? vbConfig.CONTEXT_ROOT : '/';

      if (!contextRoot.startsWith('/')) {
        contextRoot = `/${contextRoot}`;
      }
      if (!contextRoot.endsWith('/')) {
        contextRoot = `${contextRoot}/`;
      }

      return contextRoot;
    }

    /**
     * Returns an array of service worker plugin URLs specified in the userConfig.configuration.serviceWorkerConfig
     * object in the application model JSON file.
     *
     * @param {Object} config - The userConfig.configuration.serviceWorkerConfig object.
     {boolean} [isAnonymous=false] - Whether the application is anonymous.
     * @returns {Promise<Array>} A promise that resolves with an array of service worker plugin URLs.
     */
    getServiceWorkerPlugins(config, isAnonymous = false) {
      // Get the config plugins
      return Utils.getRuntimeEnvironment()
        .then((rte) => Promise.all([super.getServiceWorkerPlugins(config, isAnonymous),
          rte.getServiceWorkerPlugins()])
          .then((pluginsArr) => {
            const plugins = pluginsArr[0];

            const vbConfig = globalThis.vbInitConfig || {};
            const versionId = vbConfig.BASE_URL_TOKEN;
            const orgId = vbConfig.ORGANIZATION_ID;
            const vbServer = vbConfig.VB_SERVER;
            const serverRoot = Utils.addTrailingSlash(Utils.cleanUpExtraSlashes(vbConfig.INGRESS_PATH || ''));

            // grab the injected CSRF token if exists
            const csrfToken = vbConfig.INJECTED && vbConfig.INJECTED.security && vbConfig.INJECTED.security.csrfToken;

            // Only install the implicit-flow handler when IDCS declares scopes it can service. This keeps the
            // compatibility path available for configured apps without enabling it as a no-op global handler.
            const allowedScopes = this.idcsInfo.allowedScopes || [];
            const implicitFlowPlugins = allowedScopes.length ? [{
              url: 'vbsw/private/plugins/implicitFlowHandlerPlugin',
              params: {
                allowedScopes,
                jwtClockSkewTolerance: this.jwtClockSkewTolerance,
              },
            }] : [];

            // the default plugins specific to VBCS
            const defaultPlugins = [
              {
                url: 'vbsw/private/plugins/sessionExpirePlugin',
                params: {
                  contextRoot: this.getContextRoot(),
                  idcsHost: this.idcsInfo.hostName,
                },
              },
              {
                url: 'vbsw/private/plugins/authPreprocessorHandlerPlugin',
                params: {
                  isAnonymous,
                  defaultAuthentication: this.defaultAuthentication,
                  passthroughs:
                    (config.configuration && config.configuration.passthroughs) || [],
                },
              },
              {
                url: 'vbsw/private/plugins/sessionTrackingHandlerPlugin',
                params: {
                  userId: this.userInfo.email,
                },
              },
              // if orgId is not undefined, install multiTenantCsrfTokenHandlerPlugin instead
              (orgId === undefined)
                ? {
                  url: 'vbsw/private/plugins/csrfTokenHandlerPlugin',
                  params: {
                    versionId,
                    csrfToken,
                  },
                }
                : {
                  url: 'vbsw/private/plugins/multiTenantCsrfTokenHandlerPlugin',
                  params: {
                    orgId,
                    serverRoot,
                    vbServer,
                  },
                },
              ...implicitFlowPlugins,
              {
                url: 'vbsw/private/plugins/tokenRelayHandlerPlugin',
                params: {
                  jwtClockSkewTolerance: this.jwtClockSkewTolerance,
                },
              },
              {
                url: 'vbsw/private/plugins/authHeaderHandlerPlugin',
                params: {
                  isAnonymous,
                },
              },
              {
                url: 'vbsw/private/plugins/oAuthAccessTokenHandlerPlugin',
                params: {
                  useAccessToken: !!this.accessToken,
                },
              },
              {
                url: 'vbsw/private/plugins/resourceChangedPlugin',
                params: {
                  contextRoot: this.getContextRoot(),
                  versionId,
                },
              },
            ];

            // add the default plugins
            defaultPlugins.forEach((plugin) => {
              if (plugins.indexOf(plugin) === -1) {
                plugins.push(plugin);
              }
            });

            // add additional plugins provided via runtime environment
            plugins.push(...pluginsArr[1]);

            // Make sure no vb- headers used for intra plugin communication escape
            plugins.push('vbsw/private/plugins/authPostprocessorHandlerPlugin');
            // install workaround for Chrome bug and make sure we can diagnose errors
            plugins.push('vbsw/private/plugins/errorResponseHandlerPlugin');
            return plugins;
          }));
    }

    /**
     * Handles the logout process for the application.
     *
     * If application OAuth flow is enabled, removes the refresh token key from storage before logging out.
     *
     * @param {String} [logoutUrl] - The home URL of the application.
     * @returns {(Promise<void>|void)} A promise that resolves when the logout process is complete,
     * or void if no asynchronous operation is performed.
     */
    handleLogout(logoutUrl) {
      if (this.enableApplicationOAuthFlow) {
        return this.removeRefreshToken()
          .then(() => {
            this.removeCodeVerifier();
            return super.handleLogout(logoutUrl);
          });
      }

      return super.handleLogout(logoutUrl);
    }

    /**
     * Handles the 'vbSessionExpired' message from the service worker.
     *
     * This method redirects the user to the login URL when the session expires.
     *
     * @returns {Promise<boolean|String>} - A promise that resolves to true if the session is refreshed successfully,
     * false if the session refresh fails, or rejects with an error message.
     */
    vbSessionExpired() {
      const loginUrl = this.getLoginUrl();

      // go through runtimeEnvironment to give DT a chance to handle session expiry
      return Application.runtimeEnvironment.sessionExpired(loginUrl);
    }

    /**
     * Refreshes the implicit flow access token for the specified scope.
     *
     * This method handles the 'vbRefreshImplicitFlowAccessToken' message from the ImplicitFlowHandlerPlugin
     * and returns a promise that resolves with the refreshed access token.
     *
     * @param {String} scope - The scope for which to refresh the access token.
     * @returns {Promise<String>} A promise that resolves with the refreshed access token.
     */
    vbRefreshImplicitFlowAccessToken(scope) {
      return new Promise((resolve, reject) => {
        // get the hidden iframe for refreshing the access token
        const refreshTokenIframe = document.getElementById(REFRESH_ACCESS_TOKEN_IFRAME);

        if (refreshTokenIframe) {
          let timeout;

          // Bind the hidden iframe authorization response to this refresh attempt.
          const state = DefaultSecurityProvider.generateAndStoreOAuthState(this.getImplicitOAuthStateStorageKey(scope));

          // The message must come from the iframe created for this token refresh, not just any same-origin window.
          const refreshTokenWindow = /** @type {HTMLIFrameElement} */ (refreshTokenIframe).contentWindow;

          // listener for waiting for the access token from the hidden iframe
          const listener = (e) => {
            const { data } = e;

            if (e.source !== refreshTokenWindow
              || e.origin !== globalThis.location.origin
              || !data
              || typeof data !== 'object'
              || data.method !== 'vbRefreshAuthToken'
              || !Array.isArray(data.args)) {
              return;
            }

            const accessTokenHash = data.args[0];

            if (typeof accessTokenHash !== 'string' || !accessTokenHash) {
              return;
            }

            // clear the timeout and remove the listener
            clearTimeout(timeout);
            globalThis.removeEventListener('message', listener);

            // parse the hash containing the access token
            const fragments = Router.parseFragment(accessTokenHash);

            // The iframe response must include the state generated for this refresh request.
            const validState = DefaultSecurityProvider.validateAndConsumeOAuthState(
              this.getImplicitOAuthStateStorageKey(scope), fragments.state,
            );
            if (!validState) {
              reject(new Error('OAuth state validation failed.'));
              return;
            }

            // Do not construct a malformed Authorization header from an incomplete redirect response.
            if (!fragments.token_type || !fragments.access_token) {
              reject(new Error('OAuth access token response is missing required fields.'));
              return;
            }

            // extract the new access token from the hash
            const token = `${fragments.token_type} ${fragments.access_token}`;

            resolve(token);
          };
          globalThis.addEventListener('message', listener);

          // redirect the hidden iframe to the authorization url
          const authUrl = this.getOAuthAuthorizationUrl(scope, 'token', '', 'S256', state);
          // @ts-ignore to fix TS2339: Property 'src' does not exist on type 'HTMLElement'
          refreshTokenIframe.src = authUrl;

          timeout = setTimeout(() => {
            globalThis.removeEventListener('message', listener);
            DefaultSecurityProvider.consumeOAuthState(this.getImplicitOAuthStateStorageKey(scope));
            reject(new Error('Refreshing access token timed out.'));

            // either we got redirected to the login page because the user is not logged in,
            // or the consent form if the scope is being access for the first time, simply redirect
            // the application to the authorization url so the user can log in or respond to the
            // consent form
            DefaultSecurityProvider.redirectToOAuthAuthorizationUrl(authUrl).catch(() => {});
          }, REFRESH_ACCESS_TOKEN_TIMEOUT);
        }
      });
    }

    /**
     * Obtains an OAuth access token by running the authorization code with PKCE flow.
     *
     * This method checks if an access token already exists, retrieves a refresh token if one doesn't exist,
     * exchanges the authorization code for an access token, generates a code verifier and challenge,
     * redirects to the authorization URL, or refreshes the access token using the refresh token.
     *
     * @returns {Promise<String>} A promise resolving with the obtained OAuth access token.
     */
    vbGetOAuthAccessToken() {
      return Promise.resolve().then(() => {
        if (this.accessToken) {
          return this.accessToken;
        }

        return this.retrieveRefreshToken()
          .then((refreshToken) => {
            if (!refreshToken) {
              const authCode = Router.consumeAuthCode();

              if (authCode) {
                // Validate the authorization response before exchanging the code.
                const validState = DefaultSecurityProvider.validateAndConsumeOAuthState(
                  this.oAuthCodeStateStorageKey, Router.consumeAuthState(),
                );
                if (!validState) {
                  throw new Error('OAuth state validation failed.');
                }

                // exchange the authorization code for an access token
                return this.exchangeOAuthAccessToken(authCode);
              }

              let vbScope = this.idcsInfo.allowedScopes
                .find((scope) => scope.endsWith('urn:opc:resource:consumer::all'));
              vbScope = `${vbScope} offline_access`; // need to append offline_access to get refresh token

              const codeVerifier = this.generateAndStoreCodeVerifier();
              // Store state alongside the code verifier so the redirect back to VB-RT can be validated.
              const state = DefaultSecurityProvider.generateAndStoreOAuthState(this.oAuthCodeStateStorageKey);

              return CryptoUtils.generateCodeChallenge(codeVerifier)
                .then((codeChallenge) => {
                  const authUrl = this.getOAuthAuthorizationUrl(vbScope, 'code', codeChallenge, 'S256', state);

                  return DefaultSecurityProvider.redirectToOAuthAuthorizationUrl(authUrl);
                });
            }

            // use the refresh token to get a new access token
            return this.vbRefreshOAuthAccessToken();
          });
      });
    }

    /**
     * Redirects to the authorization URL to obtain an authorization code, which will reload the application.
     *
     * @static
     * @param {String} authUrl - The URL of the authorization endpoint.
     * @returns {Promise<void>} A rejected promise to stop initialization after the redirect starts.
     * @throws {Error} An error indicating that the application is being redirected to the authorization URL.
     */
    static redirectToOAuthAuthorizationUrl(authUrl) {
      return Promise.resolve().then(() => {
        globalThis.location.replace(authUrl);

        throw new Error('Redirecting to authorization url, stopping further initialization.');
      });
    }

    /**
     * Exchanges an authorization code for an access token and a refresh token.
     *
     * @param {String} authCode - The authorization code to exchange for tokens.
     * @returns {Promise<String>} A promise that resolves with the obtained access token.
     * @throws {Error} If the code verifier cannot be retrieved or if the token exchange fails.
     */
    exchangeOAuthAccessToken(authCode) {
      return Promise.resolve().then(() => {
        const { tokenExchangeUrl } = this;
        if (!tokenExchangeUrl) {
          throw new Error('Token exchange URL is not configured.');
        }

        const codeVerifier = this.consumeCodeVerifier();
        if (!codeVerifier) {
          throw new Error('Failed to retrieve code verifier.');
        }

        const request = new Request(tokenExchangeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `code=${authCode}&code_verifier=${codeVerifier}&scope=`,
        });

        return fetch(request)
          .then((response) => {
            if (response.ok) {
              return response.json()
                .then((tokenInfo) => {
                  this.accessToken = tokenInfo.access_token;

                  return this.storeRefreshToken(tokenInfo.refresh_token)
                    .then(() => this.accessToken);
                });
            }

            throw new Error(`Failed to exchange token with status ${response.status}`);
          });
      });
    }

    /**
     * Gets a new access token by using the refresh token.
     *
     * This method attempts to retrieve a refresh token from storage. If successful, it uses the refresh token to
     * obtain a new access token. If the refresh token is invalid or missing, it restarts the OAuth flow.
     *
     * @returns {Promise<String>} A promise that resolves with the newly obtained access token.
     */
    vbRefreshOAuthAccessToken() {
      return this.retrieveRefreshToken()
        .then((refreshToken) => {
          // no refresh token found, restart the oauth flow
          if (!refreshToken) {
            return this.vbGetOAuthAccessToken();
          }

          const { tokenExchangeUrl } = this;
          if (!tokenExchangeUrl) {
            throw new Error('Token exchange URL is not configured.');
          }

          const request = new Request(tokenExchangeUrl, {
            method: 'POST',
            credentials: 'omit',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `refresh_token=${encodeURIComponent(refreshToken)}`,
          });

          return fetch(request)
            .then((response) => {
              if (response.ok) {
                return response.json()
                  .then((tokenInfo) => {
                    this.accessToken = tokenInfo.access_token;

                    return this.storeRefreshToken(tokenInfo.refresh_token)
                      .then(() => {
                        this.removeCodeVerifier();
                        return this.accessToken;
                      });
                  });
              }

              // invalidate the current access token and remove the refresh token from storage,
              // so we can restart the oauth flow
              this.accessToken = null;
              return this.removeRefreshToken()
                .then(() => this.vbGetOAuthAccessToken());
            });
        });
    }

    /**
     * Constructs an authorization URL for obtaining an OAuth access token for the given scope.
     *
     * When a PKCE code challenge is provided, the challenge method is always S256.
     *
     * @param {String} scope - The scope for which to request an access token.
     * @param {String} [responseType='token'] - The response type for the authorization request.
     * @param {String} [codeChallenge=''] - The code challenge for the authorization request.
     * @param {String} [codeChallengeMethod='S256'] - Retained for API compatibility; challenges always use S256.
     * @param {String} [state] - The state for the authorization request.
     * @returns {String} The constructed authorization URL.
     */
    getOAuthAuthorizationUrl(scope, responseType = 'token', codeChallenge = '', codeChallengeMethod = 'S256',
      state = '') {
      const uri = new URI(`${this.idcsInfo.hostName}/oauth2/v1/authorize`);
      // Preserve the legacy challenge-method parameter for API compatibility, but never emit a plain challenge.
      const safeCodeChallengeMethod = codeChallengeMethod === 'S256' ? codeChallengeMethod : 'S256';

      uri.addSearch('client_id', responseType === 'code'
        ? this.idcsInfo.authorizationCodeClientId : this.idcsInfo.clientId)
        .addSearch('response_type', responseType)
        .addSearch('scope', scope)
        .addSearch('redirect_uri', this.idcsInfo.oAuthRedirectEndpoint);

      if (state) {
        uri.addSearch('state', state);
      }

      if (codeChallenge) {
        uri.addSearch('code_challenge', codeChallenge);
        uri.addSearch('code_challenge_method', safeCodeChallengeMethod);
      }

      return uri.href();
    }

    /**
     * Returns an instance of a key store handler registered via vbInitConfig.KEY_STORE_HANDLER.
     *
     * @returns {Promise<{ retrieveKey: function, storeKey: function, removeKey: function }>} A promise that resolves
     * with an instance of KeyStoreHandler.
     */
    getKeyStoreHandler() {
      this.keyStoreHandlerPromise = this.keyStoreHandlerPromise || Promise.resolve()
        .then(() => {
          const vbConfig = globalThis.vbInitConfig || {};
          const path = vbConfig.KEY_STORE_HANDLER;

          if (path) {
            return Utils.getResource(path)
              .then((Module) => (typeof Module === 'function' ? new Module() : Module));
          }

          logger.warn('No KEY_STORE_HANDLER configured -- refresh tokens will not be persisted');

          // return a stub key store handler
          return {
            retrieveKey() {
              return Promise.resolve();
            },
            storeKey() {
              return Promise.resolve();
            },
            removeKey() {
              return Promise.resolve();
            },
          };
        });

      return this.keyStoreHandlerPromise;
    }

    /**
     * Returns a unique key for storing and retrieving the code verifier for this application.
     *
     * The key is constructed by concatenating the prefix "orcl.vbcs." with the client ID
     * from the IDCS info and ".codeVerifier".
     *
     * @type {String}
     */
    get codeVerifierStorageKey() {
      return `orcl.vbcs.${this.idcsInfo.authorizationCodeClientId}.codeVerifier`;
    }

    /**
     * Returns a unique key for storing and retrieving the OAuth state for the authorization code flow.
     *
     * @type {String}
     */
    get oAuthCodeStateStorageKey() {
      return `orcl.vbcs.${this.idcsInfo.authorizationCodeClientId}.oauthState`;
    }

    /**
     * Returns a unique key for storing and retrieving the OAuth state for an implicit flow token refresh.
     *
     * @param {String} scope - The scope for which the implicit flow access token is being refreshed.
     * @returns {String} The storage key for the implicit flow OAuth state.
     */
    getImplicitOAuthStateStorageKey(scope) {
      return `orcl.vbcs.${this.idcsInfo.clientId}.${scope}.oauthState`;
    }

    /**
     * Generates and stores an OAuth state value in session storage.
     *
     * @param {String} storageKey - The session storage key under which to store the state.
     * @returns {String} The generated OAuth state value.
     */
    static generateAndStoreOAuthState(storageKey) {
      const state = CryptoUtils.generateCodeVerifier();
      sessionStorage.setItem(storageKey, state);

      return state;
    }

    /**
     * Retrieves and removes an OAuth state value from session storage.
     *
     * @param {String} storageKey - The session storage key from which to consume the state.
     * @returns {String|null} The stored OAuth state value, or null if none was found.
     */
    static consumeOAuthState(storageKey) {
      const state = sessionStorage.getItem(storageKey);
      sessionStorage.removeItem(storageKey);

      return state;
    }

    /**
     * Validates the returned OAuth state against the stored value and consumes the stored value.
     *
     * @param {String} storageKey - The session storage key from which to consume the expected state.
     * @param {String} state - The OAuth state returned by the authorization response.
     * @returns {boolean} True if the returned state matches the stored state, false otherwise.
     */
    static validateAndConsumeOAuthState(storageKey, state) {
      const expectedState = DefaultSecurityProvider.consumeOAuthState(storageKey);

      return !!state && state === expectedState;
    }

    /**
     * Generates a cryptographically secure code verifier and stores it in session storage.
     *
     * @returns {String} The generated code verifier.
     */
    generateAndStoreCodeVerifier() {
      const codeVerifier = CryptoUtils.generateCodeVerifier();
      sessionStorage.setItem(this.codeVerifierStorageKey, codeVerifier);
      // Clear the legacy localStorage verifier so a new sessionStorage verifier cannot be shadowed by stale state.
      localStorage.removeItem(this.codeVerifierStorageKey);

      return codeVerifier;
    }

    /**
     * Retrieves the previously stored code verifier.
     *
     * The localStorage fallback is for an authorization redirect that started on an older runtime version before an
     * upgrade or cache refresh. The older runtime stored the verifier in localStorage, but the returned authorization
     * code may be handled by this version.
     *
     * @returns {String|null} The stored code verifier, or null if none was found.
     */
    retrieveCodeVerifier() {
      return sessionStorage.getItem(this.codeVerifierStorageKey)
        || localStorage.getItem(this.codeVerifierStorageKey);
    }

    /**
     * Removes the stored code verifier from both current and legacy storage locations.
     */
    removeCodeVerifier() {
      sessionStorage.removeItem(this.codeVerifierStorageKey);
      localStorage.removeItem(this.codeVerifierStorageKey);
    }

    /**
     * Retrieves and removes the previously stored code verifier.
     *
     * @returns {String|null} The stored code verifier, or null if none was found.
     */
    consumeCodeVerifier() {
      const codeVerifier = this.retrieveCodeVerifier();
      this.removeCodeVerifier();

      return codeVerifier;
    }

    /**
     * Retrieves the refresh token from storage using the key store handler provided by the application.
     *
     * @returns {Promise<String|null>} A promise that resolves with the refresh token, or null if none was found.
     */
    retrieveRefreshToken() {
      return this.getKeyStoreHandler()
        .then((keyStoreHandler) => keyStoreHandler.retrieveKey());
    }

    /**
     * Stores the provided refresh token securely using the key store handler provided by the application.
     *
     * @param {String} refreshToken - The refresh token to be stored.
     * @returns {Promise<void>} A promise that resolves when the refresh token has been successfully stored.
     */
    storeRefreshToken(refreshToken) {
      return this.getKeyStoreHandler()
        .then((keyStoreHandler) => keyStoreHandler.storeKey(refreshToken));
    }

    /**
     * Removes the refresh token from storage using the key store handler provided by the application.
     *
     * @returns {Promise<void>} A promise that resolves when the refresh token has been removed from storage.
     */
    removeRefreshToken() {
      return this.getKeyStoreHandler()
        .then((keyStoreHandler) => keyStoreHandler.removeKey());
    }
  }

  return DefaultSecurityProvider;
});

